import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue'
import {
  ConceptKind,
  ConceptStatus,
  useGetAccountQuery,
  useAddConceptMutation,
  useGetFundQuery,
  useGetFundSetupStateLazyQuery,
  useGetSectionWithQuestionsQuery,
  useUpdateAccountFundSetupMutation,
  useUpdateFundMutation,
  useUpdateSfdrStrategyMutation,
} from '~/types/types'
import { useShowDebugging } from '~/functions/accounts'
import { FUND_SETUP_SECTION_IDS } from '~/constants/fundSetupSections'
import { renderTrustedMarkdown } from '~/functions/markdown'


export default /*#__PURE__*/_defineComponent({
  __name: 'SectionFundSetupDynamic',
  props: {
    sectionId: null,
    fundId: null,
    accountId: null,
    portfolioId: null,
    sfdrStrategyId: null
  },
  setup(__props: any) {

const props = __props as {
  sectionId: number
  fundId?: number
  accountId?: string
  portfolioId?: string
  sfdrStrategyId?: number
};



const showDebugging = useShowDebugging()

const { mutate: updateFund } = useUpdateFundMutation()
const { mutate: updateAccount } = useUpdateAccountFundSetupMutation()
const { mutate: updateSfdrStrategy } = useUpdateSfdrStrategyMutation()
const { mutate: addConcept } = useAddConceptMutation()
const { load: loadSetupState, refetch: refetchSetupState } =
  useGetFundSetupStateLazyQuery(
    computed(() => ({
      id: props.fundId as any,
    })),
  )

const { result: fundResult } = useGetFundQuery(
  computed(() => ({ id: props.fundId as number })),
  computed(() => ({ enabled: !!props.fundId })),
)

const { result: accountResult } = useGetAccountQuery(
  computed(() => ({ id: props.accountId as string })),
  computed(() => ({ enabled: !!props.accountId })),
)

const { result: sectionResult, loading: sectionLoading } = useGetSectionWithQuestionsQuery(
  computed(() => ({
    id: props.sectionId,
    fundId: props.fundId as any,
  })),
  computed(() => ({ enabled: !!props.sectionId && !!props.fundId })),
)

const section = computed(() => sectionResult.value?.getSection || null)
const pickOverrideText = (overrideValue: any, baseValue: any) => {
  if (typeof overrideValue === 'string') {
    return overrideValue.trim().length > 0 ? overrideValue : baseValue
  }
  return overrideValue ?? baseValue
}
const sectionQuestions = computed(() =>
  (section.value?.children || [])
    .map((child: any) => {
      const question = child?.question
      if (!question) return null
      return {
        ...question,
        label: pickOverrideText(child?.label, question.label),
        sublabel: pickOverrideText(child?.sublabel, question.sublabel),
        tooltip: pickOverrideText(child?.tooltip, question.tooltip),
      }
    })
    .filter(Boolean),
)
const isSectionLoading = computed(() => !!sectionLoading.value && !section.value)
const fund = computed(() => fundResult.value?.getFund || null)
const account = computed(() => accountResult.value?.getAccount || null)
const sfdrStrategy = computed(() => {
  const allStrategies = [
    ...(fund.value?.esCharacteristics ? [fund.value.esCharacteristics] : []),
    ...(fund.value?.sustainableObjectives || []),
  ]
  if (!props.sfdrStrategyId) return allStrategies[0] || null
  return (
    allStrategies.find(
      (strategy: any) => Number(strategy?.id) === Number(props.sfdrStrategyId),
    ) || null
  )
})
const refreshSetupState = () => {
  if (!props.fundId) return
  Promise.resolve(loadSetupState() || refetchSetupState()).catch(() => {
    // no-op: refresh errors are handled by existing global UX
  })
}

const sustainableObjectiveOptions = [
  {
    id: 13613,
    label: 'Climate change mitigation (EU Taxonomy objective)',
  },
  {
    id: 13738,
    label: 'Climate change adaptation (EU Taxonomy objective)',
  },
  {
    id: 13741,
    label: 'The sustainable use and protection of water and marine resources (EU Taxonomy objective)',
  },
  {
    id: 13611,
    label: 'Transition to a circular economy (EU Taxonomy objective)',
  },
  {
    id: 13739,
    label: 'Pollution prevention and control (EU Taxonomy objective)',
  },
  {
    id: 13740,
    label: 'Protection and restoration of biodiversity and ecosystems (EU Taxonomy objective)',
  },
]

const COMPONENT_CONFIG = {
  'ix-text-field': { inputMode: 'input' as const },
  'ix-textarea': { inputMode: 'input' as const },
  'ix-form-input-combobox': { inputMode: 'change' as const },
  'ix-form-input-custom-reference-period-picker': { inputMode: 'input' as const },
  'ix-data-table-accounts': { inputMode: 'input' as const },
  'ix-form-input-large-options': { inputMode: 'input' as const },
  'ix-form-input-small-options': { inputMode: 'input' as const },
  'ix-form-input-text-master': { inputMode: 'input' as const }, // compatibility marker
  'ix-impact-concept-input': { inputMode: 'input' as const },
  'sfdr-metric-selector': { inputMode: 'input' as const },
  'kfw-ventures': { inputMode: 'input' as const },
  'ix-dialog-change-hidden-kpi-settings': { inputMode: 'input' as const },
}

const SUPPORTED_COMPONENTS = new Set(Object.keys(COMPONENT_CONFIG))
const BOOLEAN_QUESTION_IX_IDS = new Set([
  'isReportingPais',
  'isReportingKfw',
  'isConsideringSustainabilityRisk',
  'isIndexDesignated',
  'isConsideringSourcesAndEstimates',
  'isEnvironmentalGoal',
  'isCo2EmissionGoal',
  'doCheckEuTaxonomyAlignment',
])
const TOOLTIP_HELPER_TEXT_IX_IDS = new Set([
  'esCharacteristicsDescription',
  'isIndexDesignated',
  'benchmarkDescription',
  'notConsideringPaisReason',
  'entityLevelPaiConsideration',
])
const COPYABLE_TEXT_COMPONENTS = new Set(['ix-textarea', 'ix-text-field'])

const resolveEditorComponent = (question: any) => {
  const component = question?.component
  if (SUPPORTED_COMPONENTS.has(component)) return component
  console.warn(
    `[fund-setup-dynamic] Unsupported question component "${component}". Falling back to ix-text-field.`,
  )
  return 'ix-text-field'
}

const shouldHandleEvent = (question: any, eventName: 'input' | 'change') => {
  const component = resolveEditorComponent(question)
  if (component === 'ix-impact-concept-input') {
    return eventName === 'input' || eventName === 'change'
  }
  const config =
    COMPONENT_CONFIG[component as keyof typeof COMPONENT_CONFIG] ||
    COMPONENT_CONFIG['ix-text-field']
  return config.inputMode === eventName
}

const getResolvedQuestionValue = (question: any) => {
  const targetEntity = question?.targetEntity
  const targetField = question?.ixId
  if (!targetEntity || !targetField) return question?.value
  if (targetEntity === 'fund' && targetField === 'investmentPeriodId') {
    return fund.value?.investmentPeriod || null
  }
  const sourceField =
    targetEntity === 'fund' && targetField === 'fundLegalEntityIdentifier'
      ? 'legalEntityIdentifier'
      : targetEntity === 'fund' && targetField === 'socialMetricIds'
        ? 'socialMetrics'
        : targetEntity === 'fund' && targetField === 'environmentalMetricIds'
          ? 'environmentalMetrics'
          : targetEntity === 'sfdr_strategy' && targetField === 'conceptId'
            ? 'concept'
            : targetEntity === 'sfdr_strategy' && targetField === 'euTaxonomyConceptId'
              ? 'euTaxonomyConcept'
              : targetEntity === 'sfdr_strategy' &&
                  targetField === 'sustainableObjectiveVentureIds'
                ? 'ventureIds'
              : targetField

  if (targetEntity === 'fund' && fund.value) {
    const value = (fund.value as any)[sourceField]
    return value !== undefined ? value : question?.value
  }

  if (targetEntity === 'account' && account.value) {
    const value = (account.value as any)[targetField]
    return value !== undefined ? value : question?.value
  }

  if (targetEntity === 'sfdr_strategy' && sfdrStrategy.value) {
    if (targetField === 'metricIds') {
      const metricIds = (sfdrStrategy.value as any).metricIds
      if (!Array.isArray(metricIds) || metricIds.length === 0) {
        return question?.value
      }
      const metrics = (sfdrStrategy.value as any).metrics || []
      const metricsById = new Map(
        metrics.map((metric: any) => [Number(metric.id), metric]),
      )
      return metricIds.map(
        (id: number) => metricsById.get(Number(id)) ?? { id, name: `#${id}` },
      )
    }
    const value = (sfdrStrategy.value as any)[sourceField]
    return value !== undefined ? value : question?.value
  }

  return question?.value
}

const formatCompaniesSelectedSubtitle = (selectedCount: number, totalCount: number) => {
  if (totalCount <= 0) return null
  return `${selectedCount} of ${totalCount} companies selected`
}

const getVentureSelectionSubtitle = (question: any) => {
  const selected = getResolvedQuestionValue(question)
  const selectedCount = Array.isArray(selected) ? selected.length : 0

  if (question?.ixId === 'ventureIds' && question?.targetEntity === 'fund') {
    const totalCount = Array.isArray(account.value?.ventureLinks)
      ? account.value.ventureLinks.length
      : 0
    return formatCompaniesSelectedSubtitle(selectedCount, totalCount)
  }

  if (
    question?.ixId === 'sustainableObjectiveVentureIds' &&
    question?.targetEntity === 'sfdr_strategy'
  ) {
    const totalCount = Array.isArray(fund.value?.ventureIds)
      ? fund.value.ventureIds.length
      : 0
    return formatCompaniesSelectedSubtitle(selectedCount, totalCount)
  }

  return null
}

const buildQuestionProps = (question: any) => {
  const helperText = question.copyableText?.trim() || ''
  const includeHelperTextInTooltip =
    !!helperText && TOOLTIP_HELPER_TEXT_IX_IDS.has(question?.ixId)
  const tooltip = includeHelperTextInTooltip
    ? [question.tooltip, helperText].filter(Boolean).join('\n\n')
    : question.tooltip

  const ventureSelectionSubtitle = getVentureSelectionSubtitle(question)
  let subtitle = question.sublabel
  if (
    question?.ixId === 'metricIds' &&
    props.sectionId !== FUND_SETUP_SECTION_IDS.SUSTAINABLE_OBJECTIVE_TEMPLATE
  ) {
    subtitle = helperText || question.sublabel
  }
  if (ventureSelectionSubtitle) {
    const baseSubtitle = subtitle?.trim()
    subtitle = baseSubtitle
      ? `${baseSubtitle}\n\n${ventureSelectionSubtitle}`
      : ventureSelectionSubtitle
  }

  return {
    tooltip,
    id: question.id,
    title: question.label,
    subtitle,
    isMandatory: question.isMandatory,
    promptTemplate: question.promptTemplate,
    fundId: props.fundId ? Number(props.fundId) : undefined,
  }
}

const getCopyableText = (question: any) => question?.copyableText || ''

const getCopyableOptions = (question: any) => question?.copyableOptions || []

const shouldRenderCopyableHelper = (question: any) => {
  if (!question?.isCopyable) return false
  if (!COPYABLE_TEXT_COMPONENTS.has(resolveEditorComponent(question))) return false
  return !!getCopyableText(question) || getCopyableOptions(question).length > 0
}

const buildEditorProps = (question: any) => {
  const resolvedValue = getResolvedQuestionValue(question)
  const component = resolveEditorComponent(question)
  const optionValueFromIxId = (option: any) => {
    const raw =
      typeof option?.ixId === 'string'
        ? option.ixId.split('.').pop()
        : option?.id
    if (BOOLEAN_QUESTION_IX_IDS.has(question?.ixId)) {
      if (raw === 'true') return true
      if (raw === 'false') return false
    }
    return raw
  }

  if (component === 'ix-data-table-accounts') {
    if (question?.ixId === 'sustainableObjectiveVentureIds') {
      return {
        value: resolvedValue || [],
        showSelect: true,
        showPeriodSelect: false,
        filters: { fundIds: [props.fundId] },
      }
    }
    return {
      value: resolvedValue,
      showSelect: true,
      showPeriodSelect: false,
      question: {},
    }
  }

  if (component === 'ix-impact-concept-input') {
    const concept =
      resolvedValue && typeof resolvedValue === 'object'
        ? resolvedValue
        : sfdrStrategy.value?.concept ?? null
    return {
      value: concept?.id ?? null,
      impactData: { concept },
      conceptKind: ConceptKind.Impact,
      createdBy: props.portfolioId,
      sfdrStrategyId: props.sfdrStrategyId ?? null,
    }
  }

  if (component === 'ix-form-input-combobox') {
    const getCanonicalObjectiveValue = (value: any) => {
      const selectedId =
        value && typeof value === 'object' ? Number(value.id) : Number(value)
      if (!Number.isFinite(selectedId)) return value
      const matched = sustainableObjectiveOptions.find(
        (option) => Number(option.id) === selectedId,
      )
      // Preserve custom concepts; normalize only predefined taxonomy choices.
      return matched || value
    }
    if (question?.ixId === 'conceptId') {
      return {
        'item-text': 'label',
        'item-value': 'id',
        items: sustainableObjectiveOptions,
        multiple: false,
        value: getCanonicalObjectiveValue(resolvedValue),
        returnObject: true,
      }
    }
    if (question?.ixId === 'euTaxonomyConceptId') {
      return {
        'item-text': 'label',
        'item-value': 'id',
        items: sustainableObjectiveOptions,
        multiple: false,
        value: getCanonicalObjectiveValue(resolvedValue),
        returnObject: true,
      }
    }
    return {
      'item-text': 'label',
      'item-value': 'id',
      items: (question.options || []).map((option: any) => ({
        id: optionValueFromIxId(option),
        label: option.label,
      })),
      multiple: Array.isArray(resolvedValue),
      value: resolvedValue || (Array.isArray(resolvedValue) ? [] : null),
    }
  }

  if (component === 'ix-form-input-custom-reference-period-picker') {
    return {
      accountId: props.portfolioId,
      twoSelectors: true,
      period:
        resolvedValue && typeof resolvedValue === 'object' ? resolvedValue : null,
    }
  }

  if (component === 'sfdr-metric-selector') {
    const sectionByMetricIxId: Record<string, number> = {
      socialMetricIds: 91, // Additional social PAI Indicators
      environmentalMetricIds: 83, // Additional environmental PAI Indicators
    }
    const isGeneralKpis = question?.ixId === 'generalMetricIds'
    const isEsCharacteristicMetrics = question?.ixId === 'metricIds'
    const isSustainableObjectiveTemplate =
      props.sectionId === FUND_SETUP_SECTION_IDS.SUSTAINABLE_OBJECTIVE_TEMPLATE
    const metricSections = isGeneralKpis
      ? []
      : isEsCharacteristicMetrics
        ? isSustainableObjectiveTemplate
          ? [117, 82]
          : [117, 82, 967, 998, 1101]
        : sectionByMetricIxId[question.ixId]
          ? [sectionByMetricIxId[question.ixId]]
          : []
    return {
      id: question.ixId,
      fundId: props.fundId,
      query: {
        sections: metricSections,
      },
      value: Array.isArray(resolvedValue) ? resolvedValue : [],
      allowCustom: isGeneralKpis || isEsCharacteristicMetrics,
    }
  }

  if (component === 'kfw-ventures') {
    return {
      fundId: props.fundId,
      hideQuestionHeader: true,
    }
  }

  if (component === 'ix-dialog-change-hidden-kpi-settings') {
    return {
      portfolioId: props.portfolioId,
      useButton: true,
    }
  }

  if (component === 'ix-form-input-large-options') {
    return {
      value: resolvedValue,
      multiple: question?.ixId === 'esStrategy',
      allowDeselection: false,
      options: (question.options || []).map((option: any) => ({
        id: optionValueFromIxId(option),
        label: option.label,
      })),
      isEditable: true,
    }
  }

  if (component === 'ix-form-input-small-options') {
    return {
      value: resolvedValue,
      multiple: false,
      allowDeselection: false,
      options: (question.options || []).map((option: any) => ({
        value: optionValueFromIxId(option),
        label: option.label,
      })),
      disabled: false,
      itemKey: 'value',
      itemValue: 'value',
      itemText: 'label',
    }
  }

  const base: Record<string, any> = {
    value: resolvedValue,
  }
  if (component === 'ix-text-field' && question?.ixId === 'volume') {
    base.type = 'number'
  }
  return base
}

const isQuestionVisible = (question: any) => {
  if (!question?.ixId) return true
  const ixId = question.ixId
  const considering = !!fund.value?.isConsideringSustainabilityRisk
  if (
    ixId === 'sustainabilityRiskIntegrationDescription' ||
    ixId === 'likelyImpactsFromSustainabilityRisk'
  ) {
    return considering
  }
  if (ixId === 'reasonNotConsideringSustainabilityRisk') {
    return !considering
  }
  if (ixId === 'notConsideringPaisReason') {
    return fund.value?.isReportingPais === false
  }
  if (ixId === 'paiAggregationGroup') {
    return fund.value?.fundStrategy === 'ARTICLE_8_PLUS'
  }
  if (
    ixId === 'esgIntegrationDescription' ||
    ixId === 'exclusionCriteriaDescription' ||
    ixId === 'bestInClassDescription' ||
    ixId === 'normBasedScreeningDescription' ||
    ixId === 'sustainabilityLabelDescription'
  ) {
    if (
      ixId === 'exclusionCriteriaDescription' &&
      props.sectionId === FUND_SETUP_SECTION_IDS.ES_CHARACTERISTIC
    ) {
      return true
    }
    const strategy =
      sfdrStrategy.value?.esStrategy || fund.value?.esCharacteristics?.esStrategy || []
    const neededByQuestion: Record<string, string> = {
      esgIntegrationDescription: 'ESG_INTEGRATION',
      exclusionCriteriaDescription: 'EXCLUSION_CRITERIA',
      bestInClassDescription: 'BEST_IN_CLASS',
      normBasedScreeningDescription: 'NORM_BASED_SCREENING',
      sustainabilityLabelDescription: 'SUSTAINABILITY_LABEL',
    }
    return strategy.includes(neededByQuestion[ixId])
  }
  const strategy = sfdrStrategy.value
  if (strategy) {
    const isEsCharacteristicSection =
      props.sectionId === FUND_SETUP_SECTION_IDS.ES_CHARACTERISTIC
    if (['strategyDescription', 'engagementPolicyWithSpecificInvestmentStrategy'].includes(ixId)) {
      if (ixId === 'engagementPolicyWithSpecificInvestmentStrategy' && isEsCharacteristicSection) {
        return true
      }
      return !!strategy?.concept
    }
    if (ixId === 'actionsTaken') {
      if (props.sectionId === FUND_SETUP_SECTION_IDS.SUSTAINABLE_OBJECTIVE_TEMPLATE) {
        return !!strategy?.concept
      }
      return true
    }
    if (ixId === 'dueDiligenceProcessDescription') {
      if (isEsCharacteristicSection) return true
      return !!strategy?.concept && fund.value?.fundStrategy === 'ARTICLE_9'
    }
    if (ixId === 'dataSourcesConsidered' || ixId === 'shareOfDataSecondHand') {
      return !!strategy?.isConsideringSourcesAndEstimates
    }
    if (
      ['isIndexDesignated', 'sustainablyAlignedIndexExplanation', 'investmentAlignedIndexExplanation', 'broadMarketIndexComparison', 'indexCalculationMethodology'].includes(
        ixId,
      )
    ) {
      if (ixId === 'isIndexDesignated' && isEsCharacteristicSection) return true
      if (ixId === 'isIndexDesignated') return fund.value?.fundStrategy === 'ARTICLE_9'
      return fund.value?.fundStrategy === 'ARTICLE_9' && !!strategy?.isIndexDesignated
    }
    if (ixId === 'isCo2EmissionGoal' || ixId === 'doCheckEuTaxonomyAlignment') {
      return !!strategy?.isEnvironmentalGoal
    }
    if (ixId === 'benchmarkDescription') {
      return !!strategy?.isEnvironmentalGoal && !!strategy?.isCo2EmissionGoal
    }
    if (ixId === 'euTaxonomyConceptId') {
      const conceptId = Number(strategy?.concept?.id || 0)
      const inPreset = sustainableObjectiveOptions.some(
        (option) => Number(option.id) === conceptId,
      )
      return (
        !!strategy?.isEnvironmentalGoal &&
        !!strategy?.doCheckEuTaxonomyAlignment &&
        !inPreset
      )
    }
  }
  return true
}

const normalizeValue = (question: any, rawValue: any) => {
  const ixId = question?.ixId
  if (
    ixId === 'isReportingPais' ||
    ixId === 'isReportingKfw' ||
    ixId === 'isConsideringSustainabilityRisk' ||
    ixId === 'isIndexDesignated' ||
    ixId === 'isConsideringSourcesAndEstimates' ||
    ixId === 'isEnvironmentalGoal' ||
    ixId === 'isCo2EmissionGoal' ||
    ixId === 'doCheckEuTaxonomyAlignment'
  ) {
    if (rawValue === 'true') return true
    if (rawValue === 'false') return false
  }
  if (ixId === 'investmentPeriodId') return rawValue?.id ?? null
  if (ixId === 'volume') return rawValue === '' ? null : Number(rawValue)
  if (ixId === 'subsidiaries') return rawValue || []
  if (ixId === 'ventureIds') return rawValue || []
  if (ixId === 'socialMetricIds') return rawValue || []
  if (ixId === 'environmentalMetricIds') return rawValue || []
  if (ixId === 'sustainableObjectiveVentureIds') return rawValue || []
  if (ixId === 'metricIds') {
    if (!Array.isArray(rawValue)) return []
    return rawValue
      .map((item) => (typeof item === 'number' ? item : item?.id))
      .filter((id) => id !== null && id !== undefined)
  }
  if (ixId === 'conceptId') {
    if (rawValue && typeof rawValue === 'object') return rawValue.id ?? null
    return rawValue ?? null
  }
  if (ixId === 'euTaxonomyConceptId') {
    if (rawValue && typeof rawValue === 'object') return rawValue.id ?? null
    return rawValue ?? null
  }
  return rawValue === undefined ? null : rawValue
}

const isSameValue = (a: any, b: any) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a || []) === JSON.stringify(b || [])
  }
  return a === b
}

const handleQuestionEvent = (
  question: any,
  rawValue: any,
  eventName: 'input' | 'change',
) => {
  if (!isQuestionVisible(question)) return
  if (!shouldHandleEvent(question, eventName)) return

  const nextValue = normalizeValue(question, rawValue)
  const currentValue = normalizeValue(question, getResolvedQuestionValue(question))
  if (isSameValue(nextValue, currentValue)) return

  triggerUpdate(question, rawValue)
}

const applyCopyableText = (question: any, nextValue: any) => {
  if (!isQuestionVisible(question)) return
  if (nextValue === undefined || nextValue === null) return
  const normalizedNextValue = normalizeValue(question, nextValue)
  const currentValue = normalizeValue(question, getResolvedQuestionValue(question))
  if (isSameValue(normalizedNextValue, currentValue)) return
  triggerUpdate(question, nextValue)
}

const triggerUpdate = (question: any, rawValue: any) => {
  const targetEntity = question?.targetEntity
  const targetField = question?.ixId
  const updateField =
    targetEntity === 'fund' && targetField === 'fundLegalEntityIdentifier'
      ? 'legalEntityIdentifier'
      : targetEntity === 'sfdr_strategy' &&
          targetField === 'sustainableObjectiveVentureIds'
        ? 'ventureIds'
      : targetField
  if (!targetEntity || !targetField) {
    console.warn(
      '[fund-setup-dynamic] Missing question write binding metadata.',
      {
        targetEntity,
        targetField,
        questionId: question?.id,
      },
    )
    return
  }

  const value = normalizeValue(question, rawValue)

  if (targetEntity === 'sfdr_strategy' && targetField === 'conceptId') {
    if (typeof rawValue === 'number' && props.sfdrStrategyId) {
      updateSfdrStrategy({
        input: {
          id: props.sfdrStrategyId,
          conceptId: rawValue,
        },
      })
      refreshSetupState()
      return
    }
    if (typeof rawValue === 'string' && rawValue.trim()) {
      addConcept({
        input: {
          label: rawValue.trim(),
          createdBy: props.portfolioId,
          status: ConceptStatus.Proposed,
          kind: ConceptKind.Impact,
          sourceUrl: '',
          citation: '',
        },
      }).then((result: any) => {
        const conceptId = result?.data?.addConcept?.id
        if (!conceptId || !props.sfdrStrategyId) return
        updateSfdrStrategy({
          input: {
            id: props.sfdrStrategyId,
            conceptId,
          },
        })
        refreshSetupState()
      })
      return
    }
  }

  if (targetEntity === 'fund') {
    if (!props.fundId) return
    updateFund({
      input: {
        id: props.fundId,
        [updateField]: value,
      },
    })
    refreshSetupState()
    return
  }

  if (targetEntity === 'account') {
    if (!props.accountId) return
    updateAccount({
      input: {
        id: props.accountId,
        [targetField]: value,
      },
    })
    refreshSetupState()
    return
  }

  if (targetEntity === 'sfdr_strategy') {
    if (!props.sfdrStrategyId) {
      console.warn(
        '[fund-setup-dynamic] Missing sfdrStrategyId for sfdr_strategy write.',
        {
          targetField,
          questionId: question?.id,
        },
      )
      return
    }
    updateSfdrStrategy({
      input: {
        id: props.sfdrStrategyId,
        [updateField]: value,
      },
    })
    refreshSetupState()
    if (targetField === 'isEnvironmentalGoal' && value === false) {
      updateSfdrStrategy({
        input: {
          id: props.sfdrStrategyId,
          doCheckEuTaxonomyAlignment: false,
        },
      })
      refreshSetupState()
    }
    return
  }

  console.warn('[fund-setup-dynamic] Unsupported targetEntity.', {
    targetEntity,
    targetField,
    questionId: question?.id,
  })
}

return { __sfc: true,props, showDebugging, updateFund, updateAccount, updateSfdrStrategy, addConcept, loadSetupState, refetchSetupState, fundResult, accountResult, sectionResult, sectionLoading, section, pickOverrideText, sectionQuestions, isSectionLoading, fund, account, sfdrStrategy, refreshSetupState, sustainableObjectiveOptions, COMPONENT_CONFIG, SUPPORTED_COMPONENTS, BOOLEAN_QUESTION_IX_IDS, TOOLTIP_HELPER_TEXT_IX_IDS, COPYABLE_TEXT_COMPONENTS, resolveEditorComponent, shouldHandleEvent, getResolvedQuestionValue, formatCompaniesSelectedSubtitle, getVentureSelectionSubtitle, buildQuestionProps, getCopyableText, getCopyableOptions, shouldRenderCopyableHelper, buildEditorProps, isQuestionVisible, normalizeValue, isSameValue, handleQuestionEvent, applyCopyableText, triggerUpdate, renderTrustedMarkdown }
}

})